import { copperButton, sectionHeading, woodPanel } from '@/shared/ui/game-theme';
import { useEffect, useMemo, useState, type CSSProperties } from 'react';
import { t, useT } from '../i18n';
import type { Translations } from '../i18n/types';
import {
  EventPOIInnRest,
  EventPOITavernRumor,
  EventPOIVillageStop,
} from '../world-events/types';

interface WildEncounterPopupProps {
  /** Catalog event id (event.wild.<family>.v1). */
  eventId: string;
  /** Stable family slug — abandoned_village, ruined_temple, lost_chest, sanctuary, robbed_camp. */
  family: string;
  /** Optional explicit title override (used by road POIs). */
  title?: string;
  /** Optional explicit subtitle override (used by road POIs). */
  subtitle?: string;
  /** Allowed action keys (search / pray / open / rest / leave). */
  actions: string[];
  /** ISO timestamp when the prompt auto-resolves on the server. */
  promptEndsAt?: string;
  onAction: (action: string) => void;
}

const overlayStyle: CSSProperties = {
  position: 'absolute',
  inset: 0,
  zIndex: 800,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  pointerEvents: 'auto',
};

const backdropStyle: CSSProperties = {
  position: 'absolute',
  inset: 0,
  backgroundColor: 'rgba(0, 0, 0, 0.55)',
};

const cardStyle: CSSProperties = {
  position: 'relative',
  width: '85%',
  maxWidth: 340,

  padding: '22px 20px 18px',
  textAlign: 'center',
  animation: 'wild-pop 0.28s ease-out',
  ...woodPanel,
};

const titleStyle: CSSProperties = {
  fontSize: 17,

  marginBottom: 6,
  ...sectionHeading,
};

const subtitleStyle: CSSProperties = {
  fontSize: 12,
  color: '#bfb2a0',
  marginBottom: 14,
  fontStyle: 'italic',
};

const timerStyle: CSSProperties = {
  fontSize: 11,
  color: '#9f9687',
  marginBottom: 12,
};

const btnRow: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: 8,
};

const btnBase: CSSProperties = {
  width: '100%',
  padding: '10px 0',

  fontSize: 13,
  fontWeight: 700,

  cursor: 'pointer',
  transition: 'background-color 150ms ease',
  ...copperButton,
};

interface FamilyTextResolver {
  title: () => string;
  subtitle: () => string;
}

function resolveRoadPoiText(eventId: string, tr: Translations): FamilyTextResolver {
  const subtitle = () => tr.roadPoiPromptSubtitle;
  switch (eventId) {
    case EventPOIInnRest:
      return { title: () => tr.roadPoiInnTitle, subtitle };
    case EventPOITavernRumor:
      return { title: () => tr.roadPoiTavernTitle, subtitle };
    case EventPOIVillageStop:
      return { title: () => tr.roadPoiVillageTitle, subtitle };
    default:
      return { title: () => tr.roadPoiDefaultTitle, subtitle };
  }
}

function resolveFamilyText(family: string, eventId: string, tr: Translations): FamilyTextResolver {
  if (family === 'road_poi') {
    return resolveRoadPoiText(eventId, tr);
  }
  switch (family) {
    case 'abandoned_village':
      return {
        title: () => tr.wildEncounterAbandonedVillageTitle,
        subtitle: () => tr.wildEncounterAbandonedVillageSubtitle,
      };
    case 'ruined_temple':
      return {
        title: () => tr.wildEncounterRuinedTempleTitle,
        subtitle: () => tr.wildEncounterRuinedTempleSubtitle,
      };
    case 'lost_chest':
      return {
        title: () => tr.wildEncounterLostChestTitle,
        subtitle: () => tr.wildEncounterLostChestSubtitle,
      };
    case 'sanctuary':
      return {
        title: () => tr.wildEncounterSanctuaryTitle,
        subtitle: () => tr.wildEncounterSanctuarySubtitle,
      };
    case 'robbed_camp':
      return {
        title: () => tr.wildEncounterRobbedCampTitle,
        subtitle: () => tr.wildEncounterRobbedCampSubtitle,
      };
    default:
      return {
        title: () => tr.wildEncounterDefaultTitle,
        subtitle: () => '',
      };
  }
}

function actionButtonLabel(action: string, fallback: string, tr: ReturnType<typeof useT>): string {
  switch (action) {
    case 'search':
      return tr.wildActionSearch ?? 'Search';
    case 'pray':
      return tr.wildActionPray ?? 'Pray';
    case 'open':
      return tr.wildActionOpen ?? 'Open';
    case 'rest':
      return tr.wildActionRest ?? 'Rest';
    case 'leave':
      return tr.wildActionLeave ?? 'Leave';
    case 'accept':
      return tr.roadPoiActionVisit;
    case 'decline':
      return tr.roadPoiActionPassBy;
    case 'armorer':
      return tr.roadPoiActionVisitArmorer;
    case 'weapon':
    case 'weaponer':
      return tr.roadPoiActionVisitWeaponSmith;
    case 'merchant':
      return tr.roadPoiActionVisitTrader;
    case 'bounty_hunter':
      return tr.roadPoiActionVisitBountyHunter;
    default:
      return fallback;
  }
}

function actionButtonColors(action: string): { bg: string; color: string } {
  if (action === 'leave') {
    return { bg: 'rgba(180, 70, 70, 0.18)', color: '#cf9686' };
  }
  return { bg: 'rgba(70, 160, 110, 0.20)', color: '#9bb58a' };
}

export function WildEncounterPopup({
  eventId,
  family,
  title,
  subtitle,
  actions,
  promptEndsAt,
  onAction,
}: WildEncounterPopupProps) {
  const tr = useT();
  const [pendingAction, setPendingAction] = useState<string | null>(null);

  const handleAction = (action: string) => {
    if (pendingAction) return;
    setPendingAction(action);
    onAction(action);
  };

  const text = useMemo(() => resolveFamilyText(family, eventId, tr), [family, eventId, tr]);
  const useServerTitle = family !== 'road_poi' && title && title.trim().length > 0;
  const useServerSubtitle = family !== 'road_poi' && subtitle && subtitle.trim().length > 0;
  const resolvedTitle = useServerTitle ? title! : text.title();
  const resolvedSubtitle = useServerSubtitle ? subtitle! : text.subtitle();

  // Lightweight client-side countdown (mostly informational; the server is the
  // authority and will close the prompt with a timeout reason if missed).
  const [secondsLeft, setSecondsLeft] = useState<number | null>(null);

  useEffect(() => {
    if (!promptEndsAt) {
      setSecondsLeft(null);
      return;
    }
    const target = Date.parse(promptEndsAt);
    if (!Number.isFinite(target)) {
      setSecondsLeft(null);
      return;
    }
    const tick = () => {
      const left = Math.max(0, Math.ceil((target - Date.now()) / 1000));
      setSecondsLeft(left);
    };
    tick();
    const id = window.setInterval(tick, 500);
    return () => window.clearInterval(id);
  }, [promptEndsAt]);

  return (
    <>
      <style>{`
        @keyframes wild-pop {
          0%   { opacity: 0; transform: scale(0.93); }
          100% { opacity: 1; transform: scale(1); }
        }
      `}</style>
      <div style={overlayStyle}>
        <div
          style={backdropStyle}
          onClick={() => handleAction('leave')}
        />
        <div style={cardStyle} data-event-id={eventId}>
          <div style={titleStyle}>{resolvedTitle}</div>
          {resolvedSubtitle ? <div style={subtitleStyle}>{resolvedSubtitle}</div> : null}
          {secondsLeft != null ? (
            <div style={timerStyle}>{t(tr.wildEncounterPromptTimer, { seconds: secondsLeft })}</div>
          ) : null}
          <div style={btnRow}>
            {actions.map((action) => {
              const colors = actionButtonColors(action);
              const isPending = pendingAction === action;
              return (
                <button
                  key={action}
                  style={{
                ...copperButton,
                    ...btnBase,
                    backgroundColor: colors.bg,
                    color: colors.color,
                    opacity: pendingAction && !isPending ? 0.5 : 1,
                  }}
                  onClick={() => handleAction(action)}
                  disabled={pendingAction !== null}
                >
                  {actionButtonLabel(action, action, tr)}
                </button>
              );
            })}
          </div>
        </div>
      </div>
    </>
  );
}
